import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { newsArticles } from '@/mocks/sports';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = newsArticles.find((a) => a.id === id);

  if (!article) {
    return (
      <main className="pt-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-20 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
          </div>
          <h2 className="text-xl font-semibold text-foreground-900 mb-2">文章未找到</h2>
          <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm cursor-pointer">返回首页</Link>
        </div>
      </main>
    );
  }

  const relatedArticles = newsArticles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '米乐体育',
    },
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 米乐体育资讯`}
        description={article.excerpt}
        keywords={`米乐体育,${article.categoryName},${article.title},体育资讯`}
        canonicalUrl={`/article/${article.id}`}
        ogType="article"
        ogImage={article.image}
        jsonLd={articleSchema}
      />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="mb-8">
            <span className="inline-block bg-accent-50 text-accent-500 text-xs font-medium px-2.5 py-1 rounded mb-4">
              {article.categoryName}
            </span>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                {article.readTime}阅读
              </span>
            </div>
          </div>

          <div className="rounded-lg overflow-hidden mb-8">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-auto max-h-[480px] object-cover"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">相关文章</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.id}
                  to={`/article/${ra.id}`}
                  className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:shadow-md transition-all duration-200 cursor-pointer"
                >
                  <div className="h-36 overflow-hidden">
                    <img
                      src={ra.image}
                      alt={ra.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-3">
                    <h4 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
                      {ra.title}
                    </h4>
                    <p className="text-xs text-foreground-500 mt-1">{ra.date}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </article>
      </main>
    </>
  );
}